<template>
  <div class="vhx-overlay" @click.self="close">
    <div class="vhx-card">
      <div class="vhx-header">
        <h2>{{ $t('files.versionHistoryTitle') || 'Historial de versiones' }}</h2>
        <button class="vhx-close" @click="close" :aria-label="$t('buttons.close') || 'Cerrar'">×</button>
      </div>
      <div class="vhx-subtitle">{{ filename }}</div>

      <div v-if="loading" class="vhx-loading">{{ $t('files.loading') || 'Cargando…' }}</div>

      <div v-else-if="error" class="vhx-error">{{ error }}</div>

      <div v-else-if="versions.length === 0" class="vhx-empty">
        {{ $t('files.noVersions') || 'Aún no hay versiones guardadas. Se crean automáticamente cada pocos minutos mientras editas.' }}
      </div>

      <ul v-else class="vhx-list">
        <li
          v-for="v in versions"
          :key="v.handle"
          class="vhx-item"
          :class="{ 'vhx-item--selected': selectedHandle === v.handle }"
          @click="selectedHandle = v.handle"
        >
          <div class="vhx-item__main">
            <span class="vhx-item__date">{{ formatDate(v.date) }}</span>
            <span class="vhx-item__time">{{ formatTime(v.date) }}</span>
          </div>
          <span class="vhx-item__name">{{ v.name }}</span>
        </li>
      </ul>

      <div class="vhx-footer">
        <button class="vhx-btn" @click="close">
          {{ $t('buttons.cancel') || 'Cancelar' }}
        </button>
        <button
          class="vhx-btn vhx-btn--primary"
          :disabled="!selectedHandle || restoring"
          @click="restore"
        >
          {{ restoring ? ($t('files.restoring') || 'Restaurando…') : ($t('files.restoreVersion') || 'Restaurar versión') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { listVersions, readVersionBytes } from '@/utils/versionHistory';

export default {
  name: 'version-history-xlsx',
  props: {
    nodeHandle: { type: String, default: '' },
    parentHandle: { type: String, default: '' },
    filename: { type: String, default: '' },
    ibbInstance: { default: null },
  },
  data () {
    return {
      loading: true,
      error: '',
      versions: [],
      selectedHandle: '',
      restoring: false,
    };
  },
  async mounted () {
    try {
      this.versions = await listVersions({
        ibb: this.ibbInstance,
        parentHandle: this.parentHandle,
        originalName: this.filename,
      });
    } catch (e) {
      this.error = (e && e.message) || String(e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    close () { this.$emit('close'); },
    formatDate (d) {
      if (!d) return '—';
      try { return d.toLocaleDateString(); } catch (_) { return String(d); }
    },
    formatTime (d) {
      if (!d) return '';
      try { return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }); } catch (_) { return ''; }
    },
    async restore () {
      if (!this.selectedHandle || this.restoring) return;
      this.restoring = true;
      try {
        const bytes = await readVersionBytes({ ibb: this.ibbInstance, handle: this.selectedHandle });
        this.$emit('restored', bytes);
      } catch (e) {
        this.error = (e && e.message) || String(e);
      } finally {
        this.restoring = false;
      }
    },
  },
};
</script>

<style>
.vhx-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17,24,39,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}
.vhx-card {
  background: #fff;
  width: min(92vw, 520px);
  max-height: 80vh;
  border-radius: 10px;
  box-shadow: 0 24px 48px rgba(0,0,0,0.25);
  display: flex;
  flex-direction: column;
}
.vhx-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 4px;
}
.vhx-header h2 { margin: 0; font-size: 18px; color: #111827; }
.vhx-close {
  background: transparent; border: none; font-size: 24px; cursor: pointer; color: #6b7280; line-height: 1;
}
.vhx-subtitle {
  padding: 0 20px 8px; color: #6b7280; font-size: 13px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.vhx-loading, .vhx-error, .vhx-empty {
  padding: 32px 20px; text-align: center; color: #6b7280; flex: 1;
}
.vhx-error { color: #b91c1c; }
.vhx-list {
  flex: 1 1 auto;
  overflow: auto;
  margin: 0;
  padding: 4px 8px;
  list-style: none;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
}
.vhx-item {
  padding: 10px 14px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.vhx-item:hover { background: #f3f4f6; }
.vhx-item--selected { background: #dbeafe !important; }
.vhx-item__main { font-size: 14px; color: #111827; }
.vhx-item__date { font-weight: 600; }
.vhx-item__time { margin-left: 8px; color: #374151; }
.vhx-item__name { font-size: 11px; color: #9ca3af; }

.vhx-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
}
.vhx-btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #d4d4d8;
  background: #fff;
  color: #111827;
  cursor: pointer;
  font-weight: 500;
}
.vhx-btn:hover { background: #f4f4f5; }
.vhx-btn--primary {
  background: #2563eb; color: #fff; border-color: #2563eb;
}
.vhx-btn--primary:disabled { background: #9ca3af; border-color: #9ca3af; cursor: not-allowed; }
.vhx-btn--primary:hover:not(:disabled) { background: #1d4ed8; }
</style>
